// The file feed's Inventory beside the Fleetio feed's, tile by tile and line by
// line: the evidence for retiring the old board. Read-only.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import PageHeader from '../components/Shared/PageHeader';
import { fmtDate, fmtMoney, fmtNumber } from '../components/Inventory/format';
import { c, t, sp, r, card } from '../theme';

const TILES = [
  { label: 'Total value', key: 'total_value', money: true },
  { label: 'Lines', key: 'lines' },
  { label: 'Aged 6-12 months', key: 'aged_6_12', money: true },
  { label: 'Aged +12 months', key: 'aged_12_plus', money: true },
  { label: 'Critical Spare +12 months', key: 'critical_spare_12', money: true },
  { label: 'Non-Stock +45 days', key: 'non_stock_45', money: true },
  { label: 'Over maximum, excess', key: 'over_max_value', money: true },
  { label: 'Over maximum, lines', key: 'over_max_lines' },
  { label: 'No maximum set, value', key: 'no_max_value', money: true },
  { label: 'No maximum set, lines', key: 'no_max_lines' },
  { label: 'Critical spares below min, short', key: 'critical_below_min_value', money: true },
  { label: 'Critical spares below min, lines', key: 'critical_below_min_lines' },
];

const KINDS = [
  { key: 'only_file', label: 'Only in the file' },
  { key: 'only_fleetio', label: 'Only in Fleetio' },
  { key: 'quantity', label: 'Quantity differs' },
  { key: 'value', label: 'Value differs' },
  { key: 'maximum', label: 'Maximum differs' },
  { key: 'classification', label: 'Classification differs' },
  { key: 'aging', label: 'Aging bucket differs' },
  { key: 'non_stock', label: 'Non-Stock +45 differs' },
];

const BUCKETS = { aged_6_12: 'Aged 6-12', aged_12_plus: 'Aged +12', critical_spare_12: 'Critical +12', not_aged: 'Not aged' };

const show = (v, money) => (money ? fmtMoney(v) : fmtNumber(v));

function gap(file, fleetio) {
  if (!file) return fleetio ? 'new' : '0%';
  return `${(((fleetio - file) / file) * 100).toFixed(1)}%`;
}

function pair(a, b, fmt = (v) => v ?? '–') {
  return `${fmt(a)} → ${fmt(b)}`;
}

function Lines({ kind }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['inventory-fleetio', 'compare-lines', kind],
    queryFn: () => api.get('/inventory-fleetio/compare/lines', { params: { kind } }),
  });
  const [failed, setFailed] = useState('');

  async function download() {
    setFailed('');
    try {
      const blob = await api.getBlob('/inventory-fleetio/compare/lines', { params: { kind, format: 'csv' } });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory-compare-${kind}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setFailed(err.message || 'Download failed.');
    }
  }

  if (isLoading) return <p style={s.note}>Loading lines…</p>;
  if (error) return <p style={s.note}>{error.message || 'The lines could not be loaded.'}</p>;
  return (
    <div>
      <div style={s.linesHead}>
        <span style={s.note}>Showing {fmtNumber(data.rows.length)} of {fmtNumber(data.total)}, biggest value difference first.</span>
        <button type="button" onClick={download}>Download all as CSV</button>
        {failed && <span style={s.note}>{failed}</span>}
      </div>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead>
            <tr>
              {['Part', 'Description', 'Location', 'Quantity', 'Value', 'Maximum', 'Classification', 'Aging', 'Non-Stock +45', 'Last used'].map((h) => <th key={h} style={s.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((l) => (
              <tr key={`${l.part_number}|${l.location}`}>
                <td style={s.td}>{l.part_number}</td>
                <td style={s.td}>{l.description}</td>
                <td style={s.td}>{l.location}</td>
                <td style={s.td}>{pair(l.file_quantity, l.fleetio_quantity)}</td>
                <td style={s.td}>{pair(l.file_value, l.fleetio_value, (v) => (v === null ? '–' : fmtMoney(v)))}</td>
                <td style={s.td}>{pair(l.file_max, l.fleetio_max)}</td>
                <td style={s.td}>{pair(l.file_classification, l.fleetio_classification)}</td>
                <td style={s.td}>{pair(BUCKETS[l.file_bucket], BUCKETS[l.fleetio_bucket])}</td>
                <td style={s.td}>{pair(l.file_non_stock, l.fleetio_non_stock, (v) => (v === null ? '–' : fmtMoney(v)))}</td>
                <td style={s.td}>{pair(l.file_last_used, l.fleetio_last_used)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function InventoryCompare() {
  const [kind, setKind] = useState('only_file');
  const { data, isLoading, error } = useQuery({
    queryKey: ['inventory-fleetio', 'compare'],
    queryFn: () => api.get('/inventory-fleetio/compare'),
  });

  const header = (
    <PageHeader
      eyebrow="Reports"
      title="Inventory comparison"
      subtitle="The file feed against Fleetio. Each pair reads file → Fleetio."
      actions={<Link to="/inventory-fleetio" style={s.headerLink}>Inventory (Fleetio)</Link>}
    />
  );

  if (isLoading) return <div style={s.page}>{header}<p style={s.note}>Loading…</p></div>;
  if (error) return <div style={s.page}>{header}<p style={s.note}>{error.message || 'The comparison could not be loaded.'}</p></div>;
  if (!data.ready) {
    return (
      <div style={s.page}>
        {header}
        <p style={s.note}>
          Nothing to compare yet. File snapshot: {data.file_as_of ? fmtDate(data.file_as_of) : 'none'}.
          {' '}Fleetio snapshot: {data.fleetio_as_of ? fmtDate(data.fleetio_as_of) : 'none'}.
        </p>
      </div>
    );
  }

  return (
    <div style={s.page}>
      {header}
      <p style={data.same_day ? s.note : s.warning}>
        File snapshot {fmtDate(data.file_as_of)}, Fleetio snapshot {fmtDate(data.fleetio_as_of)}.
        {!data.same_day && ' These are different days, so some differences are ordinary stock movement.'}
      </p>

      <section style={s.panel}>
        <h2 style={s.title}>Board figures</h2>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Figure</th><th style={s.thNum}>File</th><th style={s.thNum}>Fleetio</th><th style={s.thNum}>Gap</th>
            </tr>
          </thead>
          <tbody>
            {TILES.map((tile) => (
              <tr key={tile.key}>
                <td style={s.td}>{tile.label}</td>
                <td style={s.tdNum}>{show(data.file[tile.key], tile.money)}</td>
                <td style={s.tdNum}>{show(data.fleetio[tile.key], tile.money)}</td>
                <td style={s.tdNum}>{gap(data.file[tile.key], data.fleetio[tile.key])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={s.panel}>
        <h2 style={s.title}>Line by line</h2>
        <p style={s.note}>{fmtNumber(data.lines.matched)} lines are in both. Choose a difference to list its lines.</p>
        <div style={s.kinds}>
          {KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              aria-pressed={kind === k.key}
              onClick={() => setKind(k.key)}
              style={kind === k.key ? s.kindOn : s.kind}
            >
              {k.label}: {fmtNumber(data.lines[k.key])}
            </button>
          ))}
        </div>
        <Lines kind={kind} />
      </section>

      <section style={s.panel}>
        <h2 style={s.title}>By cost center</h2>
        <div style={s.scroll}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Cost center</th><th style={s.thNum}>Value</th><th style={s.thNum}>Gap</th>
                <th style={s.thNum}>Aged +12</th><th style={s.thNum}>Critical +12</th>
                <th style={s.thNum}>Only in file</th><th style={s.thNum}>Only in Fleetio</th>
              </tr>
            </thead>
            <tbody>
              {data.cost_centers.map((cc) => (
                <tr key={cc.code || 'unmapped'}>
                  <td style={s.td}>{cc.code ? `${cc.code} ${cc.name}` : cc.name}</td>
                  <td style={s.tdNum}>{pair(cc.file_value, cc.fleetio_value, fmtMoney)}</td>
                  <td style={s.tdNum}>{gap(cc.file_value, cc.fleetio_value)}</td>
                  <td style={s.tdNum}>{pair(cc.file_aged_12, cc.fleetio_aged_12, fmtMoney)}</td>
                  <td style={s.tdNum}>{pair(cc.file_critical_12, cc.fleetio_critical_12, fmtMoney)}</td>
                  <td style={s.tdNum}>{fmtNumber(cc.only_file)}</td>
                  <td style={s.tdNum}>{fmtNumber(cc.only_fleetio)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const cell = { ...t.bodySm, padding: `${sp[2]} ${sp[3]}`, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' };
const head = { ...cell, color: c.textMuted, fontWeight: 600, textAlign: 'left' };
const kind = { ...t.bodySm, padding: `${sp[1]} ${sp[3]}`, border: `1px solid ${c.border}`, borderRadius: r.pill, background: c.canvas, color: c.navy, cursor: 'pointer' };

const s = {
  headerLink: { ...t.bodySm, fontWeight: 600, color: c.navy, background: c.surface, border: `1px solid ${c.border}`, borderRadius: r.md || '8px', padding: `${sp[2]} ${sp[3]}`, textDecoration: 'none', display: 'inline-block' },
  page: { display: 'flex', flexDirection: 'column', gap: sp[4] },
  note: { ...t.bodySm, color: c.textMuted, margin: 0 },
  warning: { ...t.bodySm, color: c.brandDark, margin: 0 },
  panel: { ...card.base, padding: sp[5], display: 'flex', flexDirection: 'column', gap: sp[3] },
  title: { ...t.h3, color: c.navy, margin: 0 },
  table: { width: '100%', borderCollapse: 'collapse' },
  scroll: { overflowX: 'auto' },
  th: head,
  thNum: { ...head, textAlign: 'right' },
  td: cell,
  tdNum: { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  kinds: { display: 'flex', flexWrap: 'wrap', gap: sp[2] },
  kind,
  kindOn: { ...kind, background: c.navy, color: c.onNavy },
  linesHead: { display: 'flex', alignItems: 'center', gap: sp[3], marginBottom: sp[2], flexWrap: 'wrap' },
};
